// Error Tracking groups issues by error type and message tail, so every call site that logs a
// failed request lands in a single issue called `TypeError: Failed to fetch`. That issue is
// unreadable by construction: it mixes a feature-flag poll with a token refresh, and it grows
// whenever any agent's connection drops, so its shape carries no information about whether the
// system is healthy. Giving each call site a fingerprint of its own splits them into issues
// that can be counted, watched and silenced independently.

// A request that never received an HTTP response. Each engine and each API words this
// differently and none of them carry a status, so the message is all there is to match on.
const TRANSPORT_FAILURE_PATTERNS = [
  'Failed to fetch', // Chromium, fetch()
  'NetworkError: A network error has occurred', // Amplify's wrapper
  'NetworkError when attempting to fetch resource', // Firefox, fetch()
  'Load failed', // Safari, fetch()
  'network error', // XMLHttpRequest
];

// The prefix each call site logs, and the name its issue should carry. Matching on our own log
// message is what keeps a feature-flag poll apart from a token refresh: neither the error type
// nor the stack separates them, because both are the same fetch rejection. The Unleash entry
// matches a message logged inside @unleash/proxy-client-react, which has no call site here to
// change -- the largest single contributor, and the reason this cannot be fixed by logging
// differently in our own code alone.
const CALL_SITE_NAMES: [prefix: string, name: string][] = [
  ['Unleash: unable to fetch feature toggles', 'feature-flags'],
  ['Error refreshing token', 'token-refresh'],
  ['Embedded app version check failed', 'version-check'],
  ['Unable to refresh dual channel auth session', 'dual-channel-auth'],
  ['dual-channel-auth-session', 'dual-channel-auth'],
  ['initial fetchAuthSession error', 'websocket-auth'],
  ['fetchAuthSession after auth event error', 'websocket-auth'],
  ['Error during streaming', 'summary-streaming'],
];

// Returns the Error Tracking fingerprint for a request that never reached the server, or null
// when the message describes something else -- an HTTP error carries a status and is a fault
// worth its own issue, so it keeps Datadog's own grouping.
export default function connectivityFingerprint(message: string | undefined): string | null {
  if (!message || !TRANSPORT_FAILURE_PATTERNS.some((pattern) => message.includes(pattern))) {
    return null;
  }

  const callSite = CALL_SITE_NAMES.find(([prefix]) => message.includes(prefix));
  // An unrecognised call site still groups away from application errors rather than back into
  // the catch-all issue, and its own bucket growing is the signal to name it here.
  return `connectivity/${callSite ? callSite[1] : 'unattributed'}`;
}
